import AssignmentIcon from '@mui/icons-material/Assignment';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import {
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getAppointmentTypeTranslationMap } from '../utils';
import { AvailabilityCardContentProps, Tenant } from './AvailabilityPreview.types';
import TenantSelector from './TenantSelector';
import Loader from '../../Loader';
import LocationSelector from 'FRONTEND/components/Location/LocationSelector';
import { edit as editAvailability } from 'FRONTEND/reducers/availabilitySlice';
import { selectManagedExpertById } from 'FRONTEND/reducers/expertSlice';
import { selectResourceLocations } from 'FRONTEND/reducers/locationSlice';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { Expert } from 'FRONTEND/utils/backendApi/expert/expert.types';
import { APPOINTMENT_DURATION_STEP, APPOINTMENT_TYPE_LIST, OTHER, VIDEO_CONSULTATION, VIDEO_SPECIALIST_ASSESSMENT } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';

const AvailabilityCardContent = ({
  availability,
  isEditing,
  isLoading,
  isResourceActor,
  onDateChange,
  onStartChange,
  onEndChange,
  onLocationSelect,
}: AvailabilityCardContentProps) => {
  const dispatch = useDispatch();
  const locale = useSelector((state: RootState) => state.user.locale);

  // Check location create/edit/delete rights
  const hasLocationCreateRights = useSelector(state => selectHasRight(state, [RIGHTS.LOCATIONS_CREATE, RIGHTS.LOCATIONS_UPDATE, RIGHTS.LOCATIONS_DELETE]));

  // Tenant management
  const allCurrentUserTenants = useSelector((state: RootState) => state.user.tenantList);
  const managedExpert = useSelector((state: RootState) => selectManagedExpertById(state, availability.actorId));

  // Location management
  const resourceLocationList = useSelector((state) => selectResourceLocations(state, availability.actorId));
  const availabilityLocationDisplay = availability?.location?.display;

  // Type management
  const handleAppointmentTypeListChange = (type, newValue) => {
    let newTypes;
    if (availability.typeList.includes(type) && !newValue) {
      newTypes = availability.typeList.filter(el => el !== type);
      dispatch(editAvailability({ ...availability, typeList: newTypes }));
    } else if (!availability.typeList.includes(type) && newValue) {
      newTypes = [...availability.typeList, type];
      dispatch(editAvailability({ ...availability, typeList: newTypes }));
    }
  };

  // Find tenant display name for registered availabilities.
  const findTenantNameFromId = (tenantId) => {
    const tenant: Tenant = Object.values(allCurrentUserTenants as Tenant[]).find((t: Tenant) => tenantId === t?.id);
    return tenant?.name;
  };
  return (
    <>
      {isEditing && (
        <CardContent>
          <div className='availabilityPreview-dateTimeInput'>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
              <DatePicker
                value={dayjs(availability.startDate)}
                onChange={onDateChange}
                label={i18n._('Date')}
                format='L'
                slotProps={{
                  popper: { sx: { zIndex: 3500 } },
                  textField: { size: 'small' },
                }}
              />
            </LocalizationProvider>
          </div>
          <div className='availabilityPreview-rowContent'>
            <div className='availabilityPreview-dateTimeInput'>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                <TimePicker
                  value={dayjs(availability.startDate)}
                  onChange={onStartChange}
                  label={i18n._('Start')}
                  ampm={false}
                  maxTime={dayjs(availability.endDate).set('hour', 23).set('minute', 59).set('second', 59)}
                  minutesStep={APPOINTMENT_DURATION_STEP}
                  localeText={{
                    okButtonLabel: i18n._('Ok'),
                  }}
                  slotProps={{
                    popper: { sx: { zIndex: 3500 } },
                    textField: { size: 'small' },
                  }}
                />
              </LocalizationProvider>
            </div>
            <div className='availabilityPreview-dateTimeInput'>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                <TimePicker
                  value={dayjs(availability.endDate)}
                  onChange={onEndChange}
                  label={i18n._('End')}
                  ampm={false}
                  minTime={dayjs(availability.startDate)}
                  minutesStep={APPOINTMENT_DURATION_STEP}
                  localeText={{
                    okButtonLabel: i18n._('Ok'),
                  }}
                  slotProps={{
                    popper: { sx: { zIndex: 3500 } },
                    textField: { size: 'small' },
                  }}
                />
              </LocalizationProvider>
            </div>
          </div>
        </CardContent>
      )}
      <CardContent className='availabilityPreview-content'>
        {isLoading && (
          <Loader
            position='absolute'
            isLoading={isLoading}
            transparent={false}
            noBackground={false}
            noThrottle={false}
            label=''
          />
        )}
        <Typography variant='body2' sx={{ fontWeight: 'bold', marginBottom: 1 }}>{i18n._('Appointment types')}</Typography>
        {/* // TODO: make the appointment type list dymanic,depending on tenant or user config, */}
        {/* // rather than hiding the feature for resources. */}
        {isEditing && !isResourceActor ? (
          APPOINTMENT_TYPE_LIST.map((type) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={availability.typeList.includes(type)}
                  onChange={(event) => {
                    handleAppointmentTypeListChange(type, event.target.checked);
                  }}
                />
              }
              value={type}
              key={type}
              label={getAppointmentTypeTranslationMap()[type]}
            />
          ))
        ) : (
          availability.typeList.map((type) => (
            <Typography variant='body2' key={type} sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              {(type === VIDEO_CONSULTATION) && <VideoCallIcon sx={{ marginRight: 1 }} />}
              {(type === VIDEO_SPECIALIST_ASSESSMENT) && <RecentActorsIcon sx={{ marginRight: 1 }} />}
              {(type === OTHER) && <AssignmentIcon sx={{ marginRight: 1 }} />}
              {getAppointmentTypeTranslationMap()[type]}
            </Typography>
          ))
        )}
      </CardContent>
      {isResourceActor && (
        <CardContent className='availabilityPreview-content'>
          <Typography variant='body2' sx={{ fontWeight: 'bold', marginBottom: 1 }}>{i18n._('Location')}</Typography>
          {isEditing ? (
            <LocationSelector
              selectedLocationId={availability?.location?.id}
              locationList={resourceLocationList}
              onLocationSelect={onLocationSelect}
              disableCreation={!hasLocationCreateRights}
            />
          ) : (
            <Typography variant='body2'>{availabilityLocationDisplay}</Typography>
          )}
        </CardContent>)}
      {Object.values(allCurrentUserTenants).length > 1 && !isResourceActor &&
        <CardContent className='availabilityPreview-content'>
          <Typography variant='body2' sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            {i18n._('Tenants')}
          </Typography>
          {isEditing ? (
            <TenantSelector currentAvailability={availability} allCurrentUserTenants={allCurrentUserTenants} managedExpert={managedExpert as Expert} />
          ) : (
            availability?.tenantList.length > 0 &&
            (availability?.tenantList ?? []).map((tenantId) => (
              <Typography variant='body2' key={tenantId} sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                {findTenantNameFromId(tenantId)}
              </Typography>
            ))
          )}
        </CardContent>}
    </>
  );
};

export default AvailabilityCardContent;
